<script setup>
import { computed, ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { storeToRefs } from 'pinia';
import { whenever } from "@vueuse/core";
import { uploadAttachment } from '../../services/uploadAttachment';

import AppIcon from '../atoms/app/AppIcon.vue';
import FormMessage from '../atoms/form/FormMessage.vue';
import { $unitTestSpy } from '../../utils/unit-test-spy';
import { CV_TYPES, useApplicationStore } from '../../stores/application-store';
import FileUploadDropZone from './FileUploadDropZone.vue';
import IconListItem from './IconListItem.vue';
import FileListEntry from './fileList/FileListEntry.vue';
import { track, trackVisibility } from '../../utils/tracking';
import { useBreakpoints } from '../../composables/breakpoint';
import AppBadge from '../atoms/app/AppBadge.vue';
import SelfUploadedCvEntry from './cvSelection/SelfUploadedCvEntry.vue';
import { useProfile } from '../../stores/profile-store';
import { useErrorStore } from '../../stores/error-store';
import AppInfoBox from '../molecules/app/AppInfoBox.vue';
import AppLink from '../atoms/app/AppLink.vue';
import { validateIf } from '../../utils/validators';
import ModalEditCv from './modal/ModalEditCv.vue';

defineProps({
    dropzoneHtmlElement: {
        type: HTMLElement,
    },
});

const cvEditModalOpen = ref(false);

const MIN_FILE_SIZE = 1024; // 1 KB
const MAX_FILE_SIZE_IN_MB = 8;
const MAX_FILE_SIZE = MAX_FILE_SIZE_IN_MB * (10 ** 6);

const application = useApplicationStore();

const {
    selectedCvType,
    selfUploadedCv,
    selectedCv,
    isKarriereAtCvSelected,
    isCustomCvSelected,
} = storeToRefs(application);

const uploadingFile = ref(null);
const currentlyUploading = computed(() => Boolean(uploadingFile.value));
const validationMessages = ref([]);

const { isMobile } = useBreakpoints();

function handleFileValidationError({ message, trackingElementDetail }) {
    validationMessages.value.push(message);
    trackVisibility({
        element: 'AP_F: Invalid File Upload',
        elementDetail: trackingElementDetail,
    });
}

function resetValidationMessages() {
    validationMessages.value = [];
}

function setLauraHeidelbeereValidationMessage() {
    handleFileValidationError({
        message: 'Die Lebenslauf-Vorlage von karriere.at darf nicht als Anhang verwendet werden.',
        trackingElementDetail: 'kat-cv-template',
    });
}

function validateFileSize(size, name) {
    // File has to be bigger than 1KB.
    const minFileSizeValid = size >= MIN_FILE_SIZE;
    const maxFileSizeValid = size <= MAX_FILE_SIZE;
    if (!maxFileSizeValid) {
        handleFileValidationError({
            message: `Die Dateigröße von "${name}" entspricht nicht den Anforderungen.`,
            trackingElementDetail: 'max-filesize',
        });
    }
    if (!minFileSizeValid) {
        handleFileValidationError({
            message: `Die Datei "${name}" muss eine Mindestgröße von 1 KB haben.`,
            trackingElementDetail: 'min-filesize',
        });
    }

    return maxFileSizeValid && minFileSizeValid;
}

const availableFileTypes = ['PDF', 'DOCX', 'DOC', 'ODT', 'RTF', 'JPG', 'PNG'];

function validateFileType(name) {
    const regexFileTypes = /\.(jpe?g|png|pdf|docx?|odt|rtf)$/i;
    const isValidFile = regexFileTypes.test(name);
    if (!isValidFile) {
        handleFileValidationError({
            message: `Das Dateiformat von "${name}" entspricht nicht den Anforderungen.`,
            trackingElementDetail: 'invalid-fileformat',
        });
    }

    return isValidFile;
}

let abortController;

async function uploadFile({ file }, fromDropzone) {
    $unitTestSpy(); // TODO refactor after Vue 3 upgrade

    // Validate size and type, and start upload if it is correctly
    if (validateFileSize(file.size, file.name) && validateFileType(file.name)) {
        // Initialize upload progress to signal this file is currently uploading
        uploadingFile.value.progress = 0;

        // Create a new form data element for the upload.
        const formData = new FormData();
        formData.append('file', file, file.name);

        abortController = new AbortController();

        // Upload attachment and update the progress.
        const uploadAttachmentResponse = await uploadAttachment(formData, (uploadProgress) => {
            uploadingFile.value.progress = uploadProgress;
        }, abortController)
            .then(response => response)
            .catch((exception) => {
                if (exception.response?.data?.errors?.[0].title === 'Nice try, Laura Heidelbeere.') {
                    setLauraHeidelbeereValidationMessage();
                } else if (exception.code !== 'ERR_CANCELED') {
                    handleFileValidationError({
                        message: `Die Datei "${file.name}" entspricht nicht den Anforderungen.`,
                        trackingElementDetail: 'wrong-file',
                    });
                }
            });

        if (uploadAttachmentResponse) {
            selfUploadedCv.value = uploadAttachmentResponse;
            selectedCvType.value = CV_TYPES.CUSTOM;

            track({
                element: 'Cv Upload',
                elementDetail: fromDropzone ? 'Drag Drop' : 'File Browser',
            });
        }
    }

    uploadingFile.value = null;
}

async function handleUpload(files, fromDropzone) {
    if (!files.length || currentlyUploading.value) {
        return;
    }

    // Cleanup of old validation messages.
    resetValidationMessages();

    // take first file if user drag + dropped multiple
    uploadingFile.value = {
        file: files[0],
        progress: undefined,
    };

    // Upload the file.
    await uploadFile(uploadingFile.value, fromDropzone);
}

const errorState = useErrorStore();

const uploadNotPossible = computed(() => currentlyUploading.value);
const waitingForCv = computed(() => application.cvFile.isDownloading || application.isCvRendering);
const isKarriereAtCvValid = computed(() => !application.hasCvRenderError && !errorState.hasCvLoadingError);

const validations = {
    uploadingFile: {
        mustNotBeUploading: uploading => !uploading,
    },
    renderError: {
        noRenderError: validateIf(
            isKarriereAtCvSelected,
            () => !application.hasCvRenderError,
        ),
    },
    loadingError: {
        noLoadingError: validateIf(
            isKarriereAtCvSelected,
            () => !errorState.hasCvLoadingError,
        ),
    },
    selectedCv: {
        notWaitingForCv: validateIf(isKarriereAtCvSelected, () => !waitingForCv.value),
        anyCvFileSelected: cv => cv !== null,
        maxSize: (cv) => {
            if (!cv) return true;
            return cv.size <= MAX_FILE_SIZE;
        },
    },
};

const state = computed(() => ({
    selectedCv,
    renderError: application.hasCvRenderError,
    uploadingFile,
}));

const v$ = useVuelidate(validations, state, { $registerAs: 'cv' });

// Don't show error message for mandatory CV, if CV is still downloading/rendering
whenever(waitingForCv, () => {
    v$.value.selectedCv.$reset();
});

function updateValidation() {
    resetValidationMessages();
    v$.value.$touch();
}

function abortUpload() {
    abortController?.abort();
}

/* k.at CV functionality */
function selectKarriereAtCv() {
    selectedCvType.value = CV_TYPES.KARRIEREAT;
}

function editCv() {
    cvEditModalOpen.value = true;
}

const karriereAtCvAriaLabel = computed(() => `Meinen karriere.at Lebenslauf verwenden
    ${isKarriereAtCvSelected.value ? ' - aktuell ausgewählte Option.' : '.'}`);

/* self uploaded CV functionality */
function selectSelfUploadedCv() {
    selectedCvType.value = CV_TYPES.CUSTOM;
}

const profile = useProfile();

function removeSelfUploadedCv() {
    selfUploadedCv.value = null;
    if (profile.hasProfile) {
        selectedCvType.value = CV_TYPES.KARRIEREAT;
    } else {
        selectedCvType.value = CV_TYPES.NONE;
    }
    v$.value.$reset();
}

const selfUploadedCvAriaLabel = computed(() => `Meine hochgeladene Datei ${selfUploadedCv.value.name} als
    Lebenslauf verwenden ${isCustomCvSelected.value ? ' - aktuell ausgewählt.' : '.'}`);

// Used by parent & unit tests
defineExpose({
    setLauraHeidelbeereValidationMessage,
});
</script>

<template>
    <div>
        <FileUploadDropZone
            v-if="dropzoneHtmlElement"
            :visible=" !uploadNotPossible"
            :target-element="dropzoneHtmlElement"
            @dropped="handleUpload($event, true)"
        >
            <template #title>
                Lebenslauf hier ablegen
            </template>
            <template #subtitle>
                <div class="flex gap-xs justify-center flex-wrap mt-xs mb-xs">
                    <AppBadge
                        v-for="fileType in availableFileTypes"
                        :key="fileType"
                        outline
                    >
                        {{ fileType }}
                    </AppBadge>
                </div>
                max. 8 MB
            </template>
        </FileUploadDropZone>
        <!-- k.at CV -->
        <template v-if="profile.hasProfile">
            <div
                class="relative"
                data-qa="karriere at cv box"
            >
                <button
                    type="button"
                    data-qa="use karriere at cv"
                    class="absolute inset-0"
                    :aria-label="karriereAtCvAriaLabel"
                    @click="selectKarriereAtCv"
                />
                <FileListEntry
                    class="p-l border-solid rounded"
                    :data-qa="`karriere at cv ${!isKarriereAtCvSelected ? 'not' : ''} selected`"
                    :class="{
                        'has-status-error': v$.$error,
                        // eslint-disable-next-line max-len,vue/max-len
                        'border-2 border-green-700': !v$.renderError.$invalid && !v$.loadingError.$invalid && isKarriereAtCvSelected,
                        'border-2 border-error-border': v$.renderError.$invalid || v$.loadingError.$invalid,
                        'border border-gray-200': !isKarriereAtCvSelected,
                    }"
                    :underline-title="isKarriereAtCvValid"
                    :bold-title="isKarriereAtCvSelected && !v$.$invalid"
                    :file="application.cvFile"
                    :hide-actions="!isKarriereAtCvValid || waitingForCv"
                    @attachment-changed="updateValidation"
                    @edit-cv="editCv"
                />
            </div>
            <AppInfoBox
                v-if="v$.renderError.$invalid || v$.loadingError.$invalid"
                class="mt-m"
                warning
            >
                <template #icon>
                    <AppIcon name="alert"/>
                </template>
                <AppLink
                    tag="button"
                    class="k-c-infoBox__link cursor-pointer"
                    aria-label="Lebenslauf noch einmal generieren"
                    data-qa="retry cv render"
                    @click="application.renderCv"
                >
                    Versuche es nochmal
                </AppLink>
                oder lade einen eigenen Lebenslauf hoch.
            </AppInfoBox>
            <template v-if="v$.selectedCv.$error">
                <template v-if="v$.selectedCv.notWaitingForCv.$invalid">
                    <FormMessage
                        class="my-s"
                        data-qa="cv selection form message cv currently rendering"
                        type="error"
                    >
                        Dein karriere.at Lebenslauf wird gerade neu generiert.
                        Bitte warte bis der Vorgang abgeschlossen ist.
                    </FormMessage>
                </template>
            </template>
            <template v-if="v$.renderError.$error">
                <FormMessage
                    class="my-s"
                    data-qa="cv selection form message cv render error"
                    type="error"
                >
                    Beim Erstellen deines karriere.at Lebenslaufs ist etwas schief gegangen.
                    Deine letzten Änderungen wurden nicht übernommen.
                </FormMessage>
            </template>
            <template v-if="v$.loadingError.$error">
                <FormMessage
                    class="my-s"
                    data-qa="cv selection form message cv render error"
                    type="error"
                >
                    Beim Laden deines karriere.at Lebenslaufs ist etwas schief gegangen.
                </FormMessage>
            </template>

            <div
                class="flex gap-s items-center my-s"
                data-qa="cv selection divider"
            >
                <hr class="k-c-divider k-c-divider--fine k-c-divider--full border-0">
                <span class="text-s text-gray-600 flex-shrink-0">
                    oder
                </span>
                <hr class="k-c-divider k-c-divider--fine k-c-divider--full border-0">
            </div>
        </template>

        <!-- Self upload functionality -->
        <FileListEntry
            v-if="uploadingFile"
            data-qa="uploading cv box"
            :class="{
                'p-l rounded border-solid': profile.hasProfile,
                'border-2 border-green-700': isCustomCvSelected,
                'border border-gray-200': !isCustomCvSelected,
            }"
            :file="uploadingFile.file"
            :is-currently-uploading="uploadingFile.progress !== undefined"
            :upload-progress="uploadingFile.progress ?? 0"
            @abort-upload="abortUpload"
        />
        <div
            v-else-if="selfUploadedCv"
            class="relative"
        >
            <button
                data-qa="use self uploaded cv"
                type="button"
                class="absolute inset-0"
                :aria-label="selfUploadedCvAriaLabel"
                @click="selectSelfUploadedCv"
            />
            <SelfUploadedCvEntry
                :data-qa="`self upload cv ${!isCustomCvSelected ? 'not' : ''} selected`"
                :class="{
                    'p-l rounded border-solid': profile.hasProfile,
                    'border-2 border-green-700': isCustomCvSelected,
                    'border border-gray-200': !isCustomCvSelected,
                }"
                :bold-title="isCustomCvSelected"
                :file="selfUploadedCv"
                @remove-uploaded-cv="removeSelfUploadedCv"
            />
        </div>
        <label
            v-else
            class="cursor-pointer block"
            :class="{
                'p-l rounded border-solid': profile.hasProfile,
                'border-2 border-green-700': isCustomCvSelected,
                'border border-gray-200': !isCustomCvSelected,
            }"
            data-qa="self cv upload box"
        >
            <IconListItem
                underline-title
                align-actions-top
                :class="{
                    'has-status-error': v$.selectedCv.$error,
                }"
                :error="v$.selectedCv.$error"
            >
                <template
                    v-if="!isMobile"
                    #icon
                >
                    <div class="bg-white inset-0 w-full h-full flex items-center justify-center">
                        <AppIcon
                            :name="v$.selectedCv.$error ? 'alert' : 'plus'"
                            size="xl"
                            :class="v$.selectedCv.$error && 'text-error-text'"
                        />
                    </div>
                </template>
                <template #title>
                    <div class="flex flex-col">
                        Lebenslauf hochladen
                        <input
                            :disabled="uploadNotPossible"
                            accept="image/jpeg, image/png, application/pdf,
                                    .doc, .docx, .odt, text/rtf, application/rtf"
                            class="u-screen-reader-only u-screen-reader-only--focusable"
                            data-qa="file input"
                            type="file"
                            @change="handleUpload($event.target.files)"
                        >
                    </div>
                </template>
                <template #subtitle>
                    Füge deinen Lebenslauf hinzu, PDF empfohlen (Max. {{ MAX_FILE_SIZE_IN_MB }} MB)
                </template>
            </IconListItem>
        </label>

        <!-- VALIDATION MESSAGES -->
        <FormMessage
            v-if="validationMessages.length"
            class="my-s"
            data-qa="cv selection form message"
            type="error"
        >
            <div
                v-for="validationMessage in validationMessages"
                :key="validationMessage"
            >
                {{ validationMessage }}
            </div>
        </FormMessage>
        <template v-if="v$.selectedCv.$error">
            <FormMessage
                v-if="v$.selectedCv.anyCvFileSelected.$invalid"
                class="my-s"
                data-qa="cv selection form message no cv selected"
                type="error"
            >
                Bitte wähle einen Lebenslauf aus, den du mit deiner Bewerbung senden möchtest.
            </FormMessage>
            <FormMessage
                v-if="v$.selectedCv.maxSize.$invalid"
                class="my-s"
                data-qa="cv selection form message max size"
                type="error"
            >
                Dein Lebenslauf darf nicht größer als {{ MAX_FILE_SIZE_IN_MB }} MB sein.
            </FormMessage>
        </template>
        <ModalEditCv
            v-model:open="cvEditModalOpen"
        />
    </div>
</template>

<style lang="scss">
@import '~@karriereat/global-styles/scss/components/divider';
</style>
